import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import SavingOverlay from "../SavingOverlay";
import { Toast } from "../Toast";
import { useToast } from "../../hooks/useToast";
import {
  entityUserService,
  OptionItem,
} from "../../services/entityUserService";
import { colors } from "../../theme/designSystem";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  ueprId: string;
  userName: string;
  profiles: OptionItem[];
  initialProfileIds: string[];
  teams: OptionItem[];
  initialTeamIds: string[];
  onSaved: () => void;
  // Quando o usuario e o unico Adm. Cliente ativo da entidade, o perfil admin nao pode ser desmarcado.
  lockAdminUncheck: boolean;
}

const EntityUserProfilesModal: React.FC<Props> = ({
  isOpen,
  onClose,
  ueprId,
  userName,
  profiles,
  initialProfileIds,
  teams,
  initialTeamIds,
  onSaved,
  lockAdminUncheck,
}) => {
  const { toast, showToast, hideToast } = useToast();
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelected(initialProfileIds);
      setSelectedTeamIds(initialTeamIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ueprId]);

  // true quando este checkbox e o perfil admin travado e esta marcado (nao pode desmarcar).
  const isAdminLocked = (p: OptionItem) =>
    lockAdminUncheck && !!p.isAdmin && selected.includes(p.id);

  const professionalSelected = profiles.some(
    (p) => p.isProfessional && selected.includes(p.id)
  );
  // Equipes não são persistidas por aqui (master ainda é o Bubble) — sem trava.
  const teamMissing = false;

  const toggle = (id: string) => {
    const prof = profiles.find((p) => p.id === id);
    if (prof && isAdminLocked(prof)) return; // admin unico ativo: nao pode desmarcar
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    if (saving) return;
    if (teamMissing) {
      showToast("Selecione ao menos uma equipe para o profissional.", "error");
      return;
    }
    setSaving(true);
    try {
      await entityUserService.updateProfiles(
        ueprId,
        selected,
        professionalSelected ? selectedTeamIds : []
      );
      showToast("Perfis atualizados com sucesso", "success");
      onSaved();
      onClose();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao salvar os perfis. Tente novamente.";
      showToast(message, "error");
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        if (!saving) onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "12px", position: "relative", overflow: "hidden" },
      }}
    >
      <SavingOverlay open={saving} />
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: "white",
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          Perfis — {userName}
        </Box>
        <IconButton
          onClick={onClose}
          disabled={saving}
          sx={{
            color: "white",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
          Marque os perfis que o usuário deve ter nesta entidade.
        </Typography>
        {profiles.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: colors.textSecondary, textAlign: "center", py: 2 }}
          >
            Nenhum perfil disponível.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {profiles.map((p) => {
              const locked = isAdminLocked(p);
              const label = (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selected.includes(p.id)}
                      onChange={() => toggle(p.id)}
                      sx={{
                        color: locked ? colors.textSecondary : colors.primary,
                        "&.Mui-checked": {
                          color: locked ? colors.textSecondary : colors.primary,
                        },
                        ...(locked ? { opacity: 0.6 } : {}),
                      }}
                    />
                  }
                  label={p.name}
                  sx={
                    locked
                      ? { cursor: "not-allowed", color: colors.textSecondary }
                      : undefined
                  }
                />
              );

              const showTeams = !!p.isProfessional && selected.includes(p.id);

              return (
                <React.Fragment key={p.id}>
                  {locked ? (
                    <Tooltip title="A entidade deve ter pelo menos um Adm. Cliente ativo.">
                      {label}
                    </Tooltip>
                  ) : (
                    label
                  )}
                  {showTeams && (
                    <Box sx={{ ml: 4, mb: 1, mt: 0.5 }}>
                      <Autocomplete
                        multiple
                        size="small"
                        options={teams}
                        getOptionLabel={(o) => o.name}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        value={teams.filter((t) =>
                          selectedTeamIds.includes(t.id)
                        )}
                        onChange={(_, vals) =>
                          setSelectedTeamIds(vals.map((v) => v.id))
                        }
                        // O tema global fixa height nos OutlinedInput nao-multiline; no Autocomplete
                        // multiplo isso esmaga os chips e eles vazam para fora da borda. Casar o mesmo
                        // seletor do tema garante o override por ordem de origem: altura cresce e os
                        // chips quebram dentro da borda.
                        sx={{
                          "& .MuiOutlinedInput-root, & .MuiOutlinedInput-root:not(.MuiInputBase-multiline)":
                            {
                              height: "auto",
                              minHeight: 40,
                              alignItems: "center",
                              flexWrap: "wrap",
                              py: "3px",
                            },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Equipe(s)"
                            placeholder={
                              selectedTeamIds.length > 0
                                ? undefined
                                : "Selecione a(s) equipe(s)"
                            }
                            required
                            margin="dense"
                            error={teamMissing}
                            helperText={
                              teamMissing
                                ? "Selecione ao menos uma equipe."
                                : undefined
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: "1rem 1.5rem", gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          sx={{ textTransform: "none" }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            backgroundColor: colors.primary,
            textTransform: "none",
            "&.Mui-disabled": {
              backgroundColor: colors.primary,
              opacity: 0.7,
              color: "white",
            },
          }}
        >
          {saving ? "Processando..." : "Salvar"}
        </Button>
      </DialogActions>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Dialog>
  );
};

export default EntityUserProfilesModal;
